import { lazy, Suspense, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router-dom";
import RoleLogin from "./LoggingForm/RoleLogin";
import Dashboard from "./Dashboard";
import Council from "./Council";
// import UploadAnalysis from "./UploadAnalysis";
import ChatwithAi from "./ChatwithAi.jsx";
import PaymentPage from "./Payment/index.jsx";
import withAuth from "../hoc/withAuth.jsx";
import AuthContext from "../context/AuthContext.jsx";
import AdminAuthContext from "../context/AdminAuthContext.jsx";
import CounsellorAuthContext from "../context/CounsellorAuthContext.jsx";
import SkillProviderAuthContext from "../context/SkillProviderAuthContext.jsx";
import ChannelPartnerAuthContext from "../context/ChannelPartnerAuthContext.jsx";
import SchoolAuthContext from "../context/SchoolAuthContext.jsx";
import LeaderTraits from "./LeaderTraits/index.jsx";
import AICareerBuddy from "./AICareerBuddy/index.jsx";
// Lazy-loaded: Career Guide bundles ~10k lines of career/college/scholarship
// data, so it's split into its own chunk instead of growing the main bundle
// every user downloads on first load.
const CareerGuideHome = lazy(() => import("./CareerGuide/CareerGuideHome.jsx"));
const CareerGuideRoadmap = lazy(() => import("./CareerGuide/CareerGuideRoadmap.jsx"));
import Profile from "./Profile/index.jsx";
import Assessment from "./Assessment/index.jsx";
import KnowTheProcess from "./HowItWorks/index.jsx";
import PortalFeedbackForm from "./Feedback/index.jsx";
import Reports from "./Reports/index.jsx";
import CareerSkills from "../CareerSkills/index.jsx";
import Milestone from "./Milestone/index.jsx";
import Announcement from "./Announcement/index.jsx";
import AdminLoginPage from "./AdminDashboard/AdminLoginPage.jsx";
import AdminDashboardPage from "./AdminDashboard/AdminDashboardPage.jsx";
import CounsellorLoginPage from "./CounsellorPortal/CounsellorLoginPage.jsx";
import CounsellorDashboardPage from "./CounsellorPortal/CounsellorDashboardPage.jsx";
import SkillProviderLoginPage from "./SkillProviderPortal/SkillProviderLoginPage.jsx";
import SkillProviderDashboardPage from "./SkillProviderPortal/SkillProviderDashboardPage.jsx";
import ChannelPartnerLoginPage from "./ChannelPartnerPortal/ChannelPartnerLoginPage.jsx";
import ChannelPartnerPortalPage from "./ChannelPartnerPortal/ChannelPartnerPortalPage.jsx";
import ChannelPartnerCommunityPartnersPage from "./ChannelPartnerPortal/ChannelPartnerCommunityPartnersPage.jsx";
import ChannelPartnerEarningDetailsPage from "./ChannelPartnerPortal/ChannelPartnerEarningDetailsPage.jsx";
import ChannelPartnerProfessionalWorkPage from "./ChannelPartnerPortal/ChannelPartnerProfessionalWorkPage.jsx";
import SchoolLoginPage from "./SchoolPortal/SchoolLoginPage.jsx";
import SchoolDashboardPage from "./SchoolPortal/SchoolDashboardPage.jsx";
import ParentLoginPage from "./ParentPortal/ParentLoginPage.jsx";
import ParentPortalPage from "./ParentPortal/ParentPortalPage.jsx";
import NonLoggedInLayout from "../layout/NonLoggedInLayout";
import { validateCommunityPartnerReferral } from "../services/channelPartnerPortalApi.js";

const ProtectedDashboard = withAuth(Dashboard, ["Student", "Parent", "School", ]);
const ProtectedCouncil = withAuth(Council, ["Admin","Student","Parent","School"]);
const ProtectedLeaderTraits = withAuth(LeaderTraits, ["Admin","Student","Parent","School"]);
// const ProtectedUpload = withAuth(UploadAnalysis, ["School"]);
const ProtectedChatbot = withAuth(ChatwithAi, ["Student", "Parent"]);
const ProtectedAICareerBuddy = withAuth(AICareerBuddy, ["Student", "Parent"]);
const ProtectedCareerGuideHome = withAuth(CareerGuideHome, ["Student", "Parent"]);
const ProtectedCareerGuideRoadmap = withAuth(CareerGuideRoadmap, ["Student", "Parent"]);
const ProtectedProfile = withAuth(Profile, ["Student", "Parent"]);
const ProtectedAssessment = withAuth(Assessment, ["Student", "Parent"]);
const ProtectedHowItWorks = withAuth(KnowTheProcess, ["Student", "Parent"]);
const ProtectedFeedback = withAuth(PortalFeedbackForm, ["Student", "Parent"]);
const ProtectedReports = withAuth(Reports, ["Student", "Parent", "School", "Admin"]);
const ProtectedCareerSkills = withAuth(CareerSkills, ["Student", "Parent"]);
const ProtectedMilestone = withAuth(Milestone, ["Student", "Parent"]);
const ProtectedAnnouncement = withAuth(Announcement, ["Student", "Parent", "School", "Admin"]);

const getDefaultPath = (user) => {
  if (user?.role === "Parent") return "/parent-portal/dashboard";
  return user?.role === "Admin" ? "/admin-dashboard" : "/dashboard";
};

const AdminProtectedRoute = ({ children }) => {
  const { adminUser } = useContext(AdminAuthContext);

  if (!adminUser) {
    return <Navigate to="/admin-dashboard/login" />;
  }

  if (adminUser.role !== "Admin") {
    return <Navigate to="/" />;
  }

  return children;
};

AdminProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const CounsellorProtectedRoute = ({ children }) => {
  const { counsellorUser } = useContext(CounsellorAuthContext);

  if (!counsellorUser) {
    return <Navigate to="/counsellor-portal/login" />;
  }

  if (counsellorUser.role !== "Counsellor") {
    return <Navigate to="/counsellor-portal/login" />;
  }

  return children;
};

CounsellorProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const SkillProviderProtectedRoute = ({ children }) => {
  const { skillProviderUser } = useContext(SkillProviderAuthContext);

  if (!skillProviderUser) {
    return <Navigate to="/skill-provider-portal/login" />;
  }

  if (skillProviderUser.role !== "SkillProvider") {
    return <Navigate to="/skill-provider-portal/login" />;
  }

  return children;
};

SkillProviderProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const ChannelPartnerProtectedRoute = ({ children }) => {
  const { channelPartnerUser } = useContext(ChannelPartnerAuthContext);

  if (!channelPartnerUser || channelPartnerUser.role !== "ChannelPartner") {
    return <Navigate to="/channel-partner-portal/login" replace />;
  }

  return children;
};

ChannelPartnerProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const SchoolProtectedRoute = ({ children }) => {
  const { schoolUser } = useContext(SchoolAuthContext);

  if (!schoolUser) {
    return <Navigate to="/school-portal/login" />;
  }

  if (schoolUser.role !== "SchoolPortal") {
    return <Navigate to="/school-portal/login" />;
  }

  return children;
};

SchoolProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const ParentProtectedRoute = ({ children }) => {
  const { user } = useContext(AuthContext);

  if (!user) {
    return <Navigate to="/parent-portal/login" />;
  }

  if (user.role !== "Parent") {
    return <Navigate to="/parent-portal/login" />;
  }

  return children;
};

ParentProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};


const ProtectedPayment = withAuth(PaymentPage, ["Admin", "School"]);

const CareerGuideLoadingFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <CircularProgress sx={{ color: "#F6AA48" }} />
  </Box>
);

const SharedLoginRoute = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const requestedRole = searchParams.get("role") || "Student";

  if (user?.role === requestedRole) {
    return <Navigate to={getDefaultPath(user)} />;
  }

  return <RoleLogin forcedRole={requestedRole} />;
};

const CommunityPartnerReferralRoute = () => {
  const { user } = useContext(AuthContext);
  const { referralCode = "" } = useParams();
  const [referralStatus, setReferralStatus] = useState("loading");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (user?.role === "Student") return undefined;

    const reference = referralCode.trim();
    if (!reference) {
      setReferralStatus("invalid");
      return undefined;
    }

    let isCurrent = true;
    setReferralStatus("loading");
    validateCommunityPartnerReferral(reference)
      .then((response) => {
        if (!isCurrent) return;
        setReferralStatus(
          response?.data?.data?.is_active ? "active" : "invalid",
        );
      })
      .catch((error) => {
        if (!isCurrent) return;
        setReferralStatus(
          error?.response?.status === 404 ? "invalid" : "error",
        );
      });

    return () => {
      isCurrent = false;
    };
  }, [referralCode, retryCount, user?.role]);

  if (user?.role === "Student") {
    return <Navigate to={getDefaultPath(user)} replace />;
  }

  if (referralStatus !== "active") {
    const isLoading = referralStatus === "loading";
    const hasRequestError = referralStatus === "error";

    return (
      <NonLoggedInLayout>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 450,
              p: { xs: 3, sm: 4 },
              border: "1px solid #E8EAF0",
              borderRadius: "16px",
              textAlign: "center",
              boxShadow: "0 18px 45px rgba(45, 49, 95, 0.08)",
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={34} sx={{ color: "#F6A03A" }} />
                <Typography sx={{ mt: 2, color: "#2D315F", fontWeight: 700 }}>
                  Checking your referral link...
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  sx={{ color: "#2D315F", fontSize: 24, fontWeight: 700 }}
                >
                  {hasRequestError
                    ? "Unable to verify this link"
                    : "Referral link unavailable"}
                </Typography>
                <Typography sx={{ mt: 1.5, color: "#818FAB", lineHeight: 1.7 }}>
                  {hasRequestError
                    ? "We could not check this referral link right now. Please try again."
                    : "This referral link is invalid or is no longer active. Ask your Community Partner for a new link."}
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    gap: 1.5,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {hasRequestError && (
                    <Button
                      variant="contained"
                      onClick={() => setRetryCount((count) => count + 1)}
                      sx={{ bgcolor: "#2D315F", "&:hover": { bgcolor: "#24284D" } }}
                    >
                      Try again
                    </Button>
                  )}
                  <Button
                    component={Link}
                    to="/login?role=Student"
                    variant={hasRequestError ? "outlined" : "contained"}
                    sx={
                      hasRequestError
                        ? { borderColor: "#2D315F", color: "#2D315F" }
                        : { bgcolor: "#2D315F", "&:hover": { bgcolor: "#24284D" } }
                    }
                  >
                    Student login
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </NonLoggedInLayout>
    );
  }

  return (
    <RoleLogin
      forcedRole="Student"
      communityPartnerReferral={referralCode}
    />
  );
};

const RoutesComponent = () => {
  const { user } = useContext(AuthContext);
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={user ? <Navigate to={getDefaultPath(user)} /> : <RoleLogin />} />
        <Route path="/login" element={<SharedLoginRoute />} />
        <Route path="/r/:referralCode" element={<CommunityPartnerReferralRoute />} />
        <Route path="/admin-dashboard/login" element={<AdminLoginPage />} />
        <Route path="/counsellor-portal/login" element={<CounsellorLoginPage />} />
        <Route path="/skill-provider-portal/login" element={<SkillProviderLoginPage />} />
        <Route path="/channel-partner-portal/login" element={<ChannelPartnerLoginPage />} />
        <Route path="/school-portal/login" element={<SchoolLoginPage />} />
        <Route path="/parent-portal/login" element={<ParentLoginPage />} />
       
        {/* Protected Routes */}
        <Route
          path="/admin-dashboard/:section?"
          element={
            <AdminProtectedRoute>
              <AdminDashboardPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/counsellor-portal/:section?"
          element={
            <CounsellorProtectedRoute>
              <CounsellorDashboardPage />
            </CounsellorProtectedRoute>
          }
        />
        <Route
          path="/skill-provider-portal/:section?"
          element={
            <SkillProviderProtectedRoute>
              <SkillProviderDashboardPage />
            </SkillProviderProtectedRoute>
          }
        />
        <Route
          path="/channel-partner-portal/community-partners/work"
          element={
            <ChannelPartnerProtectedRoute>
              <ChannelPartnerCommunityPartnersPage workspaceOnly />
            </ChannelPartnerProtectedRoute>
          }
        />
        <Route
          path="/channel-partner-portal/counsellors/work"
          element={
            <ChannelPartnerProtectedRoute>
              <ChannelPartnerProfessionalWorkPage professionalType="counsellor" />
            </ChannelPartnerProtectedRoute>
          }
        />
        <Route
          path="/channel-partner-portal/skill-providers/work"
          element={
            <ChannelPartnerProtectedRoute>
              <ChannelPartnerProfessionalWorkPage professionalType="skillProvider" />
            </ChannelPartnerProtectedRoute>
          }
        />
        <Route
          path="/channel-partner-portal/earnings/details"
          element={
            <ChannelPartnerProtectedRoute>
              <ChannelPartnerEarningDetailsPage />
            </ChannelPartnerProtectedRoute>
          }
        />
        <Route
          path="/channel-partner-portal/:section?"
          element={
            <ChannelPartnerProtectedRoute>
              <ChannelPartnerPortalPage />
            </ChannelPartnerProtectedRoute>
          }
        />
        <Route
          path="/school-portal/:section?"
          element={
            <SchoolProtectedRoute>
              <SchoolDashboardPage />
            </SchoolProtectedRoute>
          }
        />
        <Route
          path="/parent-portal/:section?"
          element={
            <ParentProtectedRoute>
              <ParentPortalPage />
            </ParentProtectedRoute>
          }
        />
        <Route path="/dashboard" element={<ProtectedDashboard />} />
        <Route path="/council" element={<ProtectedCouncil />} />
        {/* <Route path="/upload" element={<ProtectedUpload />} /> */}
        <Route path="/chatbot" element={<ProtectedChatbot />} />
        <Route path="/profile" element={<ProtectedProfile />} />
        <Route path="/assessment" element={<ProtectedAssessment />} />
          <Route path="/how-it-works" element={<ProtectedHowItWorks />} />
          {/* New Routes */}

          <Route path="/reports" element={<ProtectedReports />} />
          <Route path="/career-skills" element={<ProtectedCareerSkills />} />
          <Route path="/milestone" element={<ProtectedMilestone />} />
          <Route path="/announcement" element={<ProtectedAnnouncement />} />
          <Route path="/chatbuddy" element={<ProtectedAICareerBuddy />} />
          <Route
            path="/career-guide"
            element={
              <Suspense fallback={<CareerGuideLoadingFallback />}>
                <ProtectedCareerGuideHome />
              </Suspense>
            }
          />
          <Route
            path="/career-guide/:slug"
            element={
              <Suspense fallback={<CareerGuideLoadingFallback />}>
                <ProtectedCareerGuideRoadmap />
              </Suspense>
            }
          />
          <Route path="/counseling" element={<ProtectedCouncil />} />
        
                                

        <Route path="/feedback" element={<ProtectedFeedback />} />
        <Route path="/payment" element={<ProtectedPayment />} />
        <Route path="/dna" element={<ProtectedLeaderTraits/>}/>

        {/* Catch-All Route */}
        <Route path="*" element={<p>Page not found</p>} />
      </Routes>
    </BrowserRouter>
  );
};

export default RoutesComponent;
